export const REPORT_SCHEMA_VERSION = "1.0";

export function emptyReportQuery() {
  return {
    scheduled_period_mode: "ALL",
    date_range: null,
    filters: [],
    grouping: null,
    aggregations: [],
    sort: [],
  };
}

export function serializeReportQuery(query = {}) {
  const filters = (query.filters || []).map((filter) => {
    let value = filter.value;
    if (["IN", "NOT_IN"].includes(filter.operator) && !Array.isArray(value)) {
      value = String(value || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    } else if (filter.operator === "BETWEEN" && !Array.isArray(value)) {
      value = [filter.value, filter.value_to];
    }
    return {
      field_id: filter.field_id,
      operator: filter.operator,
      ...(!["IS_NULL", "IS_NOT_NULL"].includes(filter.operator)
        ? { value }
        : {}),
    };
  });
  return {
    scheduled_period_mode: query.scheduled_period_mode || "ALL",
    ...(query.date_range ? { date_range: { ...query.date_range } } : {}),
    ...(filters.length ? { filters } : {}),
    ...(query.grouping ? { grouping: { ...query.grouping } } : {}),
    ...(query.aggregations?.length
      ? { aggregations: query.aggregations.map((item) => ({ ...item })) }
      : {}),
    ...(query.sort?.length
      ? { sort: query.sort.map((item) => ({ ...item })) }
      : {}),
  };
}

export function buildReportDocument({ catalogVersion, sheets }) {
  return {
    schema_version: REPORT_SCHEMA_VERSION,
    catalog_version: catalogVersion,
    sheets: sheets.map((sheet) => ({
      id: sheet.id,
      name: sheet.name,
      blocks: sheet.blocks.map((block) => {
        const fields = block.fields || [];
        const fieldIds =
          block.field_ids ||
          fields
            .filter((field) => field.value_source !== "MANUAL")
            .map((field) => field.id);
        const manualColumns =
          block.manual_columns ||
          fields
            .filter((field) => field.value_source === "MANUAL")
            .map((field) => ({
              id: field.id,
              label: field.label,
              data_type: field.data_type,
            }));
        const defaultColumnOrder = [
          ...fieldIds,
          ...manualColumns.map((column) => column.id),
        ];
        return {
          id: block.id,
          dataset_id: block.datasetId || block.dataset_id,
          name: block.name,
          field_ids: fieldIds,
          manual_columns: manualColumns,
          column_order: block.column_order?.length
            ? block.column_order
            : defaultColumnOrder,
          ...(block.query ? { query: serializeReportQuery(block.query) } : {}),
          position: block.position || {
            row: block.row ?? 1,
            column: block.column ?? 0,
          },
          layout_mode: block.layout_mode || block.layoutMode || "FLOW",
        };
      }),
    })),
  };
}

export function buildExploreDocument(config, catalogVersion) {
  const dimensionIds = [...config.dimensionIds];
  const dateFieldId = config.time?.fieldId || null;
  const fieldIds = [
    ...dimensionIds,
    ...(dateFieldId ? [dateFieldId] : []),
    ...config.measures.map((item) => item.fieldId),
  ];
  return buildReportDocument({
    catalogVersion,
    sheets: [
      {
        id: "insights",
        name: "Insights",
        blocks: [
          {
            id: "explore",
            dataset_id: config.datasetId,
            name: config.name || "Explore",
            field_ids: [...new Set(fieldIds)],
            query: {
              scheduled_period_mode: "ALL",
              date_range: dateFieldId
                ? {
                    field_id: dateFieldId,
                    start_date: config.startDate,
                    end_date: config.endDate,
                  }
                : null,
              filters: config.filters,
              grouping: {
                ...(dateFieldId
                  ? {
                      date_field_id: dateFieldId,
                      period: config.time.interval,
                    }
                  : {}),
                field_ids: dimensionIds,
              },
              aggregations: config.measures.map((item) => ({
                field_id: item.fieldId,
                function: item.aggregation,
              })),
              sort: config.sort,
            },
            position: { row: 1, column: 0 },
            layout_mode: "FLOW",
          },
        ],
      },
    ],
  });
}

export function exploreRequestFromDocument({
  scope,
  document,
  entityResolution = {},
  pageSize = 100,
  pageToken = null,
  execution = "SYNC",
  format = "JSON",
}) {
  const block = document?.sheets?.[0]?.blocks?.[0];
  if (!block) throw new Error("The report document needs at least one block.");
  return {
    scope,
    dataset_id: block.dataset_id,
    field_ids: block.field_ids,
    query: block.query || emptyReportQuery(),
    page_size: pageSize,
    page_token: pageToken,
    execution,
    entity_resolution: entityResolution,
    format,
  };
}

export function legacySingleStoreScope(storeId) {
  return { type: "STORES", group_id: null, store_ids: [Number(storeId)] };
}

export function definitionScope(definition, routeStoreId) {
  return definition?.scope || legacySingleStoreScope(routeStoreId);
}

export function transitionDefinitionIdentity(identity, targetSource) {
  if (!identity?.id || !identity.source || identity.source === targetSource) {
    return {
      id: identity?.id || null,
      revision: identity?.revision || null,
      source: identity?.source || null,
    };
  }
  return { id: null, revision: null, source: null };
}
